<template>
  <div class="w-full h-full">
    <div ref="container" class="h-full py-5 relative">
      <div v-if="integrationTypes.length == 0" class="w-full h-96 absolute">
        <loader />
      </div>
      <div v-else class="flex flex-grow flex-row flex-wrap">
        <div class="w-80 h-80 border border-solid border-grey-200 rounded-lg flex flex-col bg-white m-4 justify-center items-center cursor-pointer" @click="addIntegration(null, true)">
            <span class="text-6xl font-bold text-primary">+</span>
        </div>
        <info-panel
          v-for="integration in integrations"
          :key="integration.key"
          class="m-4"
        >
          <template slot="content">
            <div class="w-full h-full flex justify-center items-center bg-center bg-no-repeat" :style="{'background-image':'url('+integrationData(integration).image+')',}" style="background-size: 90% auto;"></div>
          </template>
          <template slot="footer">
            <div class="w-full h-full flex justify-between items-center">
              <div class="pl-4">{{ integration.description }}</div>
              <drop-menu :menu-items="menuItems(integration)" />
            </div>
          </template>
        </info-panel>
      </div>
      <gd-modal class="add-integration-modal" name="add-integration-modal" :title="sidebarTitle">
        <div class="flex flex-col px-8 py-5">
          <div v-if="newIntegration" class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">Type</label>
            <gd-dropdown
                v-model="type"
                :options="integrationTypes"
                size="full"
                class="h-10 add-integration-dropdown"
                :placeholder="$t('frontend_groovesell_integration_type')"
                @change="setid"
              />
          </div>
          <div class="flex flex-col w-full mb-4">
            <label class="text-grey-dark mb-2">Description</label>
            <text-input
              v-model="description"
              :placeholder="$t('frontend_groovesell_integration_description')"
            ></text-input>
          </div>
          <div class="add-integraion-configmenu">
            <config-menu
              v-if="type"
              :dark="false"
              :initial-values="initialValues"
              :plugin="type"
              :plugin-id="id"
              :resources-path="resourcesPath"
              :server-path="serverPath"
              :btnLabel="$t('frontend_groovesell_update')"
              @update="saveIntegration"
            />
          </div>
        </div>
      </gd-modal>
    </div>
    <upgrade-modal ref="upgradeModal" />
    <confirm-modal
      ref="deleteModal"
      name="delete-integration"
      :callback="confirmDelete"
    ></confirm-modal>
  </div>
</template>
<script>
import api from '@groovepages/gd-lib/api';
import auth from '@groovepages/gd-lib/auth';
import { AES } from 'crypto-js';
import encHex from 'crypto-js/enc-hex';
import padZeroPadding from 'crypto-js/pad-zeropadding';
import shortid from 'shortid';
import GdButton from './Button';
import ConfirmModal from './ConfirmModal';
import DropMenu from './DropMenu';
import GdDropdown from './Dropdown';
import InfoPanel from './InfoPanel';
import Loader from './Loader';
import ConfigMenu from './PluginMenu';
import SideBar from './SideBar';
import TextInput from './TextInput';
import UpgradeModal from './UpgradeModal';

export default {
  name: 'MyIntegrations',
  components: {
    GdButton,
    SideBar,
    ConfigMenu,
    InfoPanel,
    GdDropdown,
    TextInput,
    DropMenu,
    ConfirmModal,
    UpgradeModal,
    Loader,
  },
  data() {
    return {
      randomComputed: 0,
      containerHeight: 0,
      type: null,
      newIntegration: false,
      id: null,
      integrations: [],
      integrationTypes: [],
      description: '',
      toDelete: null,
      triggererComponentId: null,
    };
  },
  computed: {
    initialValues() {
      return { randomIncrement: this.randomComputed, new: this.newIntegration };
    },
    resourcesPath() {
      return `${window.gdEnv.VUE_APP_STORAGE_URL}/plugins/`;
    },
    serverPath() {
      return `${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/call/`;
    },
    sidebarTitle() {
      if (this.newIntegration) return this.$t('frontend_groovesell_integration_add_new');
      return this.$t('frontend_groovesell_integration_edit');
    },
  },
  created() {
    window.addEventListener('resize', this.setMaxHeight);
    this.loadIntegrations();
  },
  destroyed() {
    window.removeEventListener('resize', this.setMaxHeight);
  },
  methods: {
    loadIntegrations() {
      if (!auth.authenticatedUser()) return;
      api
        .get(
          `${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/integrations/list`
        )
        .then((result) => {
          this.integrationTypes = result.map((integration) => {
            return {
              text: integration.data.name,
              value: integration.name,
              image: integration.data.image,
            };
          }).sort((a,b) => a.text.localeCompare(b.text));
        })
        .catch(() => {});
      const metadata = auth.authenticatedUser().metadata || {};
      this.integrations = metadata.integrations || [];
    },
    menuItems(integration) {
      return [
        { text: this.$t('frontend_groovemail_forms_index_edit'), callback: () => this.editIntegration(integration) },
        { text: this.$t('frontend_delete'), callback: () => this.deleteIntegration(integration) },
      ];
    },
    integrationData(integration) {
      if (!this.integrationTypes.length) return {};
      return (
        this.integrationTypes.find((type) => type.value === integration.type) ||
        {}
      );
    },
    setid() {
      const key = encHex.parse(
        auth.authenticatedUser().id.split('-').join('').padEnd(32, '0')
      );
      const ref = this.type;
      this.type = null;
      setTimeout(() => {
        this.type = ref;
      }, 100);
      this.id = btoa(
        AES.encrypt(`integration${shortid.generate()}`, key, {
          iv: key,
          padding: padZeroPadding,
        }).toString()
      );
    },
    setMaxHeight() {
      this.$nextTick(() => {
        if (!this.$refs.integrationsContainer) return;
        const height =
          window.screen.height -
          this.$refs.integrationsContainer.offsetTop -
          (window.screen.height - this.$refs.container.clientHeight);
        this.containerHeight = `${height}px`;
      });
    },
    addIntegration(integrationName = null, intVal) {
      this.$root.$emit('modal-open', 'add-integration-modal');
      if (this.newIntegration) return;
      if (integrationName) {
        this.type = integrationName;
        this.setid();
      } else {
        this.$root.$emit('modal-open', 'add-integration-modal');
        if (this.newIntegration) return;
        if (integrationName) {
          this.type = integrationName;
          this.setid();
        } else {
          this.type = null;
        }
        this.description = '';
        if(intVal == true) {
          this.newIntegration = true;
        } else {
          this.newIntegration = false;
        }
        this.randomComputed++;
      }
    },
    editIntegration(integration) {
      this.newIntegration = false;
      this.type = null;
      this.description = integration.description;
      this.id = integration.id;
      this.randomComputed++;
      this.$nextTick(() => {
        this.type = integration.type;
      });
      this.$root.$emit('modal-open', 'add-integration-modal');
    },
    saveIntegration() {
      if (this.newIntegration) {
        this.integrations.push({
          id: this.id,
          type: this.type,
          description:
            this.description || this.integrationData({ type: this.type }).text,
        });
      } else {
        this.integrations.forEach((integration) => {
          if (integration.id === this.id)
            integration.description = this.description;
        });
      }
      this.newIntegration = false;
      this.syncIntegrations();
      if (this.triggererComponentId) {
        setTimeout(() => this.close(), 2500);
      }
    },
    deleteIntegration(integration) {
      this.toDelete = integration;
      this.$refs.deleteModal.open();
    },
    confirmDelete() {
      if (this.toDelete.id === this.id) this.$root.$emit('modal-close', 'add-integration-modal');
      this.integrations = this.integrations.filter(
        (integration) => integration.id !== this.toDelete.id
      );
      api
        .delete(
          `${window.gdEnv.VUE_APP_API_URL}/groovedigital/plugins/delete/${this.toDelete.type}/${this.toDelete.id}`
        )
        .catch(() => {});
      this.syncIntegrations();
    },
    syncIntegrations() {
      auth.updateMeta({
        integrations: this.integrations,
      });
      api
        .post(
          `${
            window.gdEnv.VUE_APP_API_URL
          }/groovedigital/auth/users/update-meta/${
            auth.authenticatedUser().id
          }`,
          {
            integrations: this.integrations.length ? this.integrations : null,
          }
        )
        .catch(() => {});
    },
    close() {
      this.newIntegration = false;
      if (this.triggererComponentId) {
        const component = this.$canvas.getComponent(this.triggererComponentId);
        component.getElement().click();
        this.triggererComponentId = null;
        this.$root.$emit('open-config');
      }
    },
    open(integration = null) {
      if (!this.integrations.length) this.loadIntegrations();
      if (integration) {
        this.$nextTick(() => {
          this.triggererComponentId = integration.pluginId;
          this.setMaxHeight();
          this.addIntegration(integration.name);
        });
      } else {
        this.$nextTick(this.setMaxHeight);
      }
    },
  },
};
</script>